/* Doctor Trigger first-party pixel — runs on every page.
 *
 * Port of assets/rrx-pixel.js from the RegenRx theme, repointed at DT
 * (pixel 408895500494422, dt-pixel-gateway).
 *
 * Why this exists: Shopify's Facebook channel pixel lives inside a sandboxed
 * web-pixel iframe, so window.fbq does not exist in page context and the theme
 * cannot fire anything through it. This file gives DT a first-party sender it
 * controls, and — via the Worker's /collect endpoint — a server-side copy of
 * every browser event that survives ad blockers and gives us exact counts.
 *
 * Each Meta event fires twice on purpose, sharing one event_id:
 *   browser -> fbq             (sets _fbp, survives if the Worker is down)
 *   server  -> Worker /collect (survives ad blockers, gives us exact counts)
 * Meta collapses that pair into a single event via the shared event_id.
 * Purchase is deliberately NOT fired here — the orders/paid webhook owns it,
 * and it is the only path that filters out Kickstarter/PledgeBox backer orders.
 * AddToCart is not fired by the PDP the way RegenRx does it either; it is
 * observed off the cart request, see "AddToCart by observation" below.
 *
 * NOTE ON DT's OTHER SENDERS (2026-08-29). Unlike RegenRx, DT keeps the
 * Facebook & Instagram channel pixel and the Omega Facebook Pixel app running
 * alongside this file. bootFbq() therefore reuses whatever fbq is already in
 * page context (Omega's, usually) rather than re-initialising the pixel.
 * Expect DT's Meta event counts to exceed real order counts by a wide margin;
 * that is Seva's deliberate choice. Judge DT performance on Shopify orders.
 *
 * The oaiq (ChatGPT Ads) calls below are inert on DT — there is no oaiq init
 * snippet, so `typeof window.oaiq === 'function'` is false and they no-op.
 * They are kept so this file stays diffable against the RegenRx original.
 */
(function () {
  var PIXEL_ID = '408895500494422';
  var COLLECT = 'https://dt-pixel-gateway.vpmouler.workers.dev/collect';

  var cfgEl = document.getElementById('dt-pixel-config');
  var cfg = {};
  try { cfg = JSON.parse((cfgEl && cfgEl.textContent) || '{}'); } catch (e) { cfg = {}; }

  function cookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m[2]) : '';
  }

  function eventId() {
    if (window.crypto && crypto.randomUUID) return 'dt-' + crypto.randomUUID();
    return 'dt-' + Date.now() + '-' + Math.random().toString(16).slice(2, 10);
  }

  function bootFbq() {
    if (typeof window.fbq === 'function') return window.fbq;
    var n = (window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!window._fbq) window._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(s);
    n('init', PIXEL_ID);
    return n;
  }

  var fbq = bootFbq();

  /* amount is integer minor units (cents). Omit amount+currency when unknown —
     OpenAI requires currency whenever amount is present. */
  function oaiqCents(n) {
    if (n == null || isNaN(n)) return undefined;
    return Math.round(Number(n) * 100);
  }

  function oaiqMeasure(name, data) {
    try {
      if (typeof window.oaiq === 'function') window.oaiq('measure', name, data);
    } catch (e) { /* never break the page */ }
  }

  function oaiqContents(custom, items) {
    var data = { type: 'contents' };
    var amt = oaiqCents(custom && custom.value);
    if (amt != null) {
      data.amount = amt;
      data.currency = (custom && custom.currency) || 'USD';
    }
    if (items && items.length) data.contents = items;
    return data;
  }

  /* fbc is only written by the pixel once it has loaded; if we arrived on an ad
     click, build it immediately in Meta's documented format so the very first
     event of the session already carries the click id.
     Last click wins: a fresh fbclid in the URL beats the _fbc cookie, which on a
     returning visitor still holds an older click and would credit the wrong ad
     (verified 2026-08-06 on RegenRx order #1261). */
  function clickId() {
    try {
      var clid = new URLSearchParams(location.search).get('fbclid');
      if (clid) return 'fb.1.' + Date.now() + '.' + clid;
    } catch (e) { /* older browsers */ }
    return cookie('_fbc');
  }

  function track(name, custom) {
    var id = eventId();
    try { fbq('track', name, custom || {}, { eventID: id }); } catch (e) { /* never break the page */ }

    if (name === 'AddToCart') {
      var ids = (custom && custom.content_ids) || [];
      oaiqMeasure('items_added', oaiqContents(custom, ids.map(function (itemId) {
        return { id: String(itemId), content_type: 'product' };
      })));
    }

    var body = JSON.stringify({
      event_name: name,
      event_id: id,
      url: location.href,
      fbp: cookie('_fbp'),
      fbc: clickId(),
      custom: custom || {}
    });
    try {
      /* text/plain keeps this a CORS "simple" request. An application/json body
         forces a preflight, which sendBeacon cannot perform — it then fails
         silently while still returning true, so the fallback never runs and every
         event is lost (verified 2026-07-27: 4 beacons sent, 0 recorded). The
         Worker reads the body as text and parses it itself, so this costs nothing. */
      var blob = new Blob([body], { type: 'text/plain' });
      if (!navigator.sendBeacon || !navigator.sendBeacon(COLLECT, blob)) {
        fetch(COLLECT, { method: 'POST', body: body, keepalive: true,
                         headers: { 'Content-Type': 'text/plain' } });
      }
    } catch (e) { /* tracking must never break the page */ }
  }

  window.dtTrack = track;

  /* ---- First-party cart stamping ----------------------------------------
   * Writes _fbc/_fbp onto the cart so Purchase attribution stops depending on
   * the Omega app staying installed. Measured 2026-08-29: DT orders carry
   * omega_fbc on 97% and _fbc on 0%; the gateway reads _fbc FIRST, so anything
   * stamped here silently takes precedence the moment it appears.
   *
   * Verified on the live store before shipping: POST /cart/update.js MERGES
   * attributes — all 11 keys owned by Omega and ABConvert survived a write.
   * It does NOT replace the bag. But an EMPTY STRING clears a key, so never
   * send a blank _fbc/_fbp.
   *
   * Deliberately NOT called on page load, which is what the RegenRx theme
   * does: on DT that would create a cart for every ad visitor and move
   * cart-creation counts that ABConvert's experiments read. Stamping happens
   * at add-to-cart and at checkout intent instead — both moments a cart
   * already exists, and no purchase can happen without passing through one.
   */
  var LANDING_TS = Date.now();
  var STAMP_KEY = 'dt-fbid-stamped';

  function hasAdParams() {
    var s = location.search;
    return s.indexOf('fbclid=') !== -1 || s.indexOf('tw_adid=') !== -1 ||
           s.indexOf('ad_id=') !== -1 || s.indexOf('utm_content=') !== -1;
  }

  function stampClickIds() {
    try {
      var fbc = clickId();
      var fbp = cookie('_fbp');
      if (!fbc && !fbp) return;
      /* Latch on the click id itself, not a boolean: a second ad click inside
         the same session must re-stamp, or the cart keeps the first click. */
      var latch = fbc || 'fbp:' + fbp;
      try { if (sessionStorage.getItem(STAMP_KEY) === latch) return; } catch (e) { /* private mode */ }
      var attrs = {};
      if (fbc) attrs._fbc = fbc;
      if (fbp) attrs._fbp = fbp;
      /* The gateway's recovery path: rebuild fbc from the fbclid in
         ad_preview_url, stamped with the true visit time from ad_landing_ts. */
      if (hasAdParams()) {
        attrs.ad_preview_url = location.origin + location.pathname + location.search;
        attrs.ad_landing_ts = String(LANDING_TS);
      }
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        /* keepalive so the write survives the full-page navigation that a
           native (non-AJAX) add-to-cart form triggers. */
        keepalive: true
      }).then(function (r) {
        if (r && r.ok) { try { sessionStorage.setItem(STAMP_KEY, latch); } catch (e) {} }
      }).catch(function () { /* tracking must never break the page */ });
    } catch (e) { /* tracking must never break the page */ }
  }

  /* ---- AddToCart by observation -----------------------------------------
   * DT's add-to-cart logic is spread across 12+ files (addons.js, cart.js,
   * product.js, sticky-atc.liquid, Shogun...), so there is no single hook to
   * patch the way RegenRx has one in regenrx-pdp.js. Observe the cart request
   * instead: nothing else in the theme has to be touched, and every ATC path
   * is covered at once, including ones added later.
   *
   * Three routes, because the theme uses all of them: fetch, XHR, and — on the
   * RegenRx Insoles PDP — a native form POST that never touches either.
   */
  var lastAtc = { key: '', t: 0 };

  /* The theme auto-adds these alongside the real product, each as its own
     /cart/add call. Left in, a $7.15 shipping-protection add-on fires its own
     AddToCart on every single order and roughly doubles the count, training
     Meta on the add-on instead of the product. Same list the gateway uses when
     deciding whether an order is nothing but Kickstarter line items. */
  var ADDON_TITLES = ['shipping protection', 'case set'];

  function isAddon(it) {
    var t = String((it && (it.product_title || it.title)) || '').toLowerCase();
    if (!t) return false;
    for (var i = 0; i < ADDON_TITLES.length; i++) {
      if (t.indexOf(ADDON_TITLES[i]) !== -1) return true;
    }
    return false;
  }

  function onCartAdd(payload) {
    try {
      var raw = (payload && payload.items) ? payload.items
              : (payload && payload.id) ? [payload] : [];
      if (!raw.length) return;
      var items = [];
      for (var k = 0; k < raw.length; k++) {
        if (!isAddon(raw[k])) items.push(raw[k]);
      }
      /* An add-on-only call is not a product add — stay silent. */
      if (!items.length) return;
      var ids = [];
      var value = 0;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var pid = it.product_id || it.id;
        if (pid) ids.push(String(pid));
        value += (Number(it.price) || 0) * (Number(it.quantity) || 1) / 100;
      }
      if (!ids.length) return;
      /* A theme that AJAX-submits fires BOTH the submit event and the request,
         which would count one add twice. Latch on the ids for a short window. */
      var key = ids.join(',');
      var now = Date.now();
      if (key === lastAtc.key && now - lastAtc.t < 2000) return;
      lastAtc = { key: key, t: now };

      track('AddToCart', {
        content_type: 'product',
        content_ids: ids,
        value: value || undefined,
        currency: cfg.currency || 'USD'
      });
      stampClickIds();
    } catch (e) { /* tracking must never break the page */ }
  }

  (function observeCartAdds() {
    if (window.__dtCartObserved) return;
    window.__dtCartObserved = true;
    var isAdd = function (u) { return String(u || '').indexOf('/cart/add') !== -1; };

    /* Every patch below delegates to the original and returns its result
       untouched — other apps on this store patch fetch/XHR too, so the chain
       must stay transparent. */
    var origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (input, init) {
        var out = origFetch.apply(this, arguments);
        try {
          var url = (input && input.url) || input;
          var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
          if (isAdd(url) && method === 'POST' && out && out.then) {
            out.then(function (res) {
              try {
                if (res && res.ok) res.clone().json().then(onCartAdd).catch(function () {});
              } catch (e) {}
            }).catch(function () {});
          }
        } catch (e) {}
        return out;
      };
    }

    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) {
      try { this.__dtUrl = u; this.__dtMethod = String(m || '').toUpperCase(); } catch (e) {}
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try {
        if (isAdd(this.__dtUrl) && this.__dtMethod === 'POST') {
          this.addEventListener('load', function () {
            try {
              if (this.status >= 200 && this.status < 300) onCartAdd(JSON.parse(this.responseText));
            } catch (e) {}
          });
        }
      } catch (e) {}
      return origSend.apply(this, arguments);
    };

    /* Native form POST (RegenRx Insoles PDP): the response is a page
       navigation, so there is no JSON to read — build the event from the form.
       track() uses sendBeacon and the stamp uses keepalive, so both survive. */
    document.addEventListener('submit', function (ev) {
      try {
        var f = ev.target;
        if (!f || !f.getAttribute) return;
        if (String(f.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
        var idEl = f.querySelector('[name="id"]');
        var qtyEl = f.querySelector('[name="quantity"]');
        onCartAdd({ items: [{
          id: idEl && idEl.value,
          product_id: cfg.product_id || (idEl && idEl.value),
          quantity: Number(qtyEl && qtyEl.value) || 1,
          price: cfg.price != null ? Math.round(Number(cfg.price) * 100) : 0
        }] });
      } catch (e) {}
    }, true);
  })();

  /* Cart total for InitiateCheckout. cfg.cart_total is only correct as of page
     RENDER — a shopper who adds to cart and then checks out would otherwise send
     the pre-add total (often 0). Refresh it from the Cart API on load, and let the
     PDP push the authoritative figure after every cart mutation. */
  var cartTotal = cfg.cart_total != null ? Number(cfg.cart_total) : null;
  window.dtSetCartTotal = function (v) {
    if (v != null && !isNaN(v)) cartTotal = Number(v);
  };
  try {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { if (c && c.total_price != null) cartTotal = c.total_price / 100; })
      .catch(function () {});
  } catch (e) { /* non-fatal */ }

  track('PageView');
  oaiqMeasure('page_viewed', oaiqContents(null, [{
    id: location.pathname || 'page',
    name: document.title || undefined,
    content_type: 'page'
  }]));

  if (cfg.product_id) {
    track('ViewContent', {
      content_type: 'product',
      content_ids: [String(cfg.product_id)],
      content_name: cfg.product_title || undefined,
      value: cfg.price != null ? Number(cfg.price) : undefined,
      currency: cfg.currency || 'USD'
    });
  }

  /* InitiateCheckout: catch the intent at click time, since Shopify's own IC
     fires server-side and late. */
  document.addEventListener('click', function (ev) {
    var el = ev.target && ev.target.closest
      ? ev.target.closest('a[href*="/checkout"], button[name="checkout"], [data-dt-checkout]')
      : null;
    if (!el) return;
    track('InitiateCheckout', {
      currency: cfg.currency || 'USD',
      value: cartTotal != null ? cartTotal : undefined
    });
    /* Second stamping point: covers a shopper who reaches checkout without a
       fresh add-to-cart (returning cart, Shop Pay, a direct /checkout link). */
    stampClickIds();
  }, true);
})();
